import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { User } from 'frontend-utils/models';
import * as LaunchDarkly from 'launchdarkly-js-client-sdk';
import ENV from 'lumen/config/environment';
import type { FeatureToggle } from 'lumen/utils/constants/feature-toggle-constants';

export default class FeatureToggleService extends Service {
  @service declare access: Services['access'];
  @service declare errorTracking: Services['error-tracking'];

  @tracked featureFlagsAvailable = false;
  @tracked launchDarklyClient: LaunchDarkly.LDClient | null = null;

  close(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.launchDarklyClient?.close();
  }

  getFeatureToggles(): LaunchDarkly.LDFlagSet | null {
    if (!this.featureFlagsAvailable) {
      this.errorTracking.captureException(
        'Feature flags not available while calling getFeatureToggles',
      );
      return null;
    }

    return this.launchDarklyClient?.allFlags() ?? null;
  }

  hasFeatureToggle(featureName: FeatureToggle): boolean {
    if (!this.featureFlagsAvailable) {
      this.errorTracking.captureException(
        'Feature flags not available while calling hasFeatureToggle',
        { featureName },
      );
      return false;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.launchDarklyClient?.variation(featureName, false) ?? false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async initialise(
    user: User,
    traits: Record<string, unknown> &
      (
        | {
            destinationSubscriptionId?: never;
            marketId?: never;
            subscriptionId: number;
          }
        | {
            destinationSubscriptionId?: never;
            marketId: number | string; // Can be a string in case of My hotels, e.g. '_135308'
            subscriptionId?: never;
          }
        | {
            destinationSubscriptionId: number;
            marketId?: never;
            subscriptionId?: never;
          }
      ),
  ) {
    try {
      this.featureFlagsAvailable = false;

      const userId = Number(user.id);

      // Only pass email for staff users, because we want to discourage targeting individual users.
      const emailStaffUser = this.access.isStaffUser ? user.email : undefined;

      const options: LaunchDarkly.LDOptions = {
        streamUrl: ENV.launchDarkly.streamUrl,
        baseUrl: ENV.launchDarkly.baseUrl,
        eventsUrl: ENV.launchDarkly.eventsUrl,
      };
      const context: LaunchDarkly.LDContext = {
        ...traits,
        userId,
        emailStaffUser,
        kind: 'user',
        key: userId.toString(),
      };
      this.launchDarklyClient = LaunchDarkly.initialize(
        ENV.launchDarkly.clientId,
        context,
        options,
      );

      const { launchDarklyClient } = this;

      return new Promise<void>((resolve) => {
        launchDarklyClient.on('ready', () => {
          // feature flags are guaranteed to be available at this point
          this.featureFlagsAvailable = true;
          resolve();
        });
      });
    } catch (error) {
      this.errorTracking.captureException(error);
      return new Promise<void>((resolve, reject) => {
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        return reject(error);
      });
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'feature-toggle': FeatureToggleService;
  }
}
